import { FC, useState, useEffect } from 'react';

interface DemoAccessTermsModalProps {
  onAccept: () => void;
}

const TERMS_ACCEPTED_KEY = 'loop_demo_terms_accepted';

export const useDemoAccessTerms = () => {
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    // Check if user has already accepted terms
    const termsAccepted = localStorage.getItem(TERMS_ACCEPTED_KEY);
    if (!termsAccepted) {
      setShowTerms(true);
    }
  }, []);

  const acceptTerms = () => {
    localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
    setShowTerms(false);
  };

  return {
    showTerms,
    acceptTerms
  };
};

export const DemoAccessTermsModal: FC<DemoAccessTermsModalProps> = ({ onAccept }) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleAccept = () => {
    if (isChecked) {
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[#182233] rounded-2xl border border-gray-600 shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-600">
          <h2 className="text-xl font-bold text-white">Demo Access Terms</h2>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4 text-gray-300 leading-relaxed">
          <p>
            By accessing this demonstration environment, you acknowledge this is a testnet preview of LOOP Chain's 
            settlement platform for evaluation purposes only.
          </p>
          
          <p>
            No real value is transferred, and all transactions use test tokens. Features shown are subject to change. 
            Your feedback helps shape our platform.
          </p>
          
          <p>
            By testing, you agree to provide insights on functionality and user experience. We may contact you regarding 
            pilot opportunities but participation creates no obligation.
          </p>
          
          <p>
            For production access or partnership discussions, please{' '}
            <a 
              href="https://loopchain.io/contact" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#2777EF] hover:text-[#4d94ff] underline transition-colors"
            >
              contact us
            </a>
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-600 bg-[#0E131A]/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Checkbox */}
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 transition-all ${
                  isChecked 
                    ? 'bg-[#2777EF] border-[#2777EF]' 
                    : 'border-gray-500 group-hover:border-gray-400'
                }`}>
                  {isChecked && (
                    <svg className="w-full h-full text-white p-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-gray-300 text-sm sm:text-base">
                I agree to{' '}
                <span className="text-[#2777EF]">demo access terms</span>
              </span>
            </label>

            {/* Accept Button */}
            <button
              onClick={handleAccept}
              disabled={!isChecked}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                isChecked
                  ? 'bg-[#2777EF] hover:bg-[#0053E2] text-white cursor-pointer'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              Accept & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoAccessTermsModal;

